import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axiosInstance from '../../../helpers/axios'
import { getSceneObjectBaseId } from '../../../utils/sceneObjectDisplayName'
import ColorSelector from '../ColorSelector'
import GraniteTextureRail from '../GraniteTextureRail'
import { useBlankMaterialCatalog } from '../../../contexts/BlankMaterialCatalogContext'
import {
    createGraniteSceneSelection,
    getGraniteMaterialKeyFromAppearance,
    getUnpolishedSurfaceCatalog,
    isSurfacePseudoCatalog,
    normalizeGraniteMaterialKey,
} from '../graniteSurfaceFinish.utils'
import {
    MONUMENT_TILE_LAYOUTS,
    MONUMENT_TILE_LAYOUT_IDS,
    MONUMENT_PORCELAIN_TILE_PRICE_RUB,
    MONUMENT_PEDESTAL_FIXED_PRICE_RUB,
    MONUMENT_LONG_CURB_UNIT_PRICE_RUB,
    MONUMENT_SHORT_CURB_FIXED_PRICE_RUB,
    MONUMENT_ASSEMBLY_PRODUCT_ROLES,
    MONUMENT_GRANITE_SOURCE,
    MONUMENT_PEDESTAL_WIDTHS_MM,
    getAutoPedestalWidthMm,
    getMonumentAssemblyCatalogLines,
    getMonumentAssemblyKnownPrice,
    getMonumentBeddingMetrics,
    getMonumentTileMetrics,
    getMonumentGraniteKeyFromAppearance,
    getMonumentGraniteOverrideKey,
    isMonumentGraniteInherited,
    normalizeMonumentAssembly,
    parseMonumentAccessorySceneId,
    patchMonumentAssembly,
} from './monumentAssembly'
import { resolveKitAssemblyLine, useScene3dKitVersion } from '../scene3dKit'

const INHERIT_PARENT_GRANITE_VALUE = '__parent_granite__'

const toPositivePrice = (value) => {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? number : 0
}

const normalizeAssemblyPriceDimensions = (value = {}) => ({
    height: Math.max(0, Math.round(Number(value?.height) || 0)),
    width: Math.max(0, Math.round(Number(value?.width) || 0)),
    thickness: Math.max(0, Math.round(Number(value?.thickness) || 0)),
})

const getAssemblyPriceDimensionKey = (value = {}) => {
    const dimensions = normalizeAssemblyPriceDimensions(value)
    const planar = [dimensions.height, dimensions.width].sort((left, right) => left - right)

    return `${planar[0]}x${planar[1]}x${dimensions.thickness}`
}

const formatAssemblyPriceDimensions = (value = {}) => {
    const dimensions = normalizeAssemblyPriceDimensions(value)
    if (!dimensions.height || !dimensions.width || !dimensions.thickness) return ''

    return `${dimensions.height}×${dimensions.width}×${dimensions.thickness} мм`
}

const getAssemblyGraniteUnitPriceMeta = ({
    catalog,
    dimensions,
    blankType = '',
} = {}) => {
    const requestedDimensions = normalizeAssemblyPriceDimensions(dimensions)
    const requestedKey = getAssemblyPriceDimensionKey(requestedDimensions)

    if (!requestedDimensions.height || !requestedDimensions.width || !requestedDimensions.thickness) {
        return {
            configured: false,
            priceLabel: 'Цена единицы не настроена',
            sizeLabel: '',
            rule: null,
        }
    }

    const normalizedBlankType = String(blankType || '').trim().toLowerCase()
    const exactRules = (Array.isArray(catalog?.sizeRules) ? catalog.sizeRules : [])
        .filter((rule) => (
            rule?.enabled !== false
            && rule?.customerVisible !== false
            && getAssemblyPriceDimensionKey(rule) === requestedKey
        ))
    const typedRules = normalizedBlankType
        ? exactRules.filter((rule) => String(rule?.blankType || '').trim().toLowerCase() === normalizedBlankType)
        : exactRules
    const candidates = typedRules.length ? typedRules : exactRules

    // Пока размеры комплекта ещё не привязаны к конкретной строке заготовки,
    // не выбираем наугад между несколькими разными вариантами одного размера.
    if (candidates.length !== 1) {
        return {
            configured: false,
            priceLabel: candidates.length > 1
                ? 'Несколько цен этого размера'
                : 'Цена этого размера не настроена',
            sizeLabel: formatAssemblyPriceDimensions(requestedDimensions),
            rule: null,
        }
    }

    const rule = candidates[0]
    const subscriberPrice = toPositivePrice(rule?.subscriberPrice)
    const publicPrice = toPositivePrice(rule?.publicPrice ?? rule?.price)
    const priceLines = []

    if (subscriberPrice > 0) {
        priceLines.push(`Подписка ${Math.round(subscriberPrice).toLocaleString('ru-RU')} ₽`)
    }
    if (publicPrice > 0) {
        priceLines.push(`Обычная ${Math.round(publicPrice).toLocaleString('ru-RU')} ₽`)
    }

    return {
        configured: priceLines.length > 0,
        priceLabel: priceLines.length ? priceLines.join('') : 'Цена этого размера не настроена',
        sizeLabel: formatAssemblyPriceDimensions(rule),
        rule,
    }
}

const EDITOR_SECTIONS = Object.freeze({
    pedestal: 'pedestal',
    bedding: 'bedding',
    foundation: 'foundation',
    tile: 'tile',
    flowerbed: 'flowerbed',
    ledger: 'ledger',
    position: 'position',
    composition: 'composition',
    brick: 'brick',
})

// Кирпич: варианты полировки по сторонам.
const BRICK_POLISH_OPTIONS = [
    { key: 'five', label: '5 ст.', hint: 'всё, кроме низа' },
    { key: 'two', label: '2 ст.', hint: 'перед и зад' },
    { key: 'front', label: '1 ст.', hint: 'только перед' },
]

const getColorSelectorValue = (value) => {
    if (value && typeof value === 'object' && value.target) {
        return value.target.value
    }

    return value ?? 'original'
}

const GraniteAssemblyMaterialSelect = ({
    label,
    material = {},
    parentMaterialKey = '',
    graniteMaterials = [],
    priceDimensions = {},
    priceBlankType = '',
    disabled = false,
    onChange,
}) => {
    const inherited = isMonumentGraniteInherited(material)
    const overrideKey = getMonumentGraniteOverrideKey(material)
    const parentKey = normalizeGraniteMaterialKey(parentMaterialKey)
    const selectedValue = inherited
        ? parentKey
        : (normalizeGraniteMaterialKey(overrideKey) || parentKey)
    const parentMaterial = graniteMaterials.find((item) => (
        normalizeGraniteMaterialKey(item?.key) === parentKey
    )) || null
    const selectedMaterial = graniteMaterials.find((item) => (
        normalizeGraniteMaterialKey(item?.key) === normalizeGraniteMaterialKey(overrideKey)
    )) || null
    const inheritedLabel = parentMaterial?.name || parentKey || 'материал стелы'

    const railItems = useMemo(() => {
        const result = []

            ; (Array.isArray(graniteMaterials) ? graniteMaterials : []).forEach((item) => {
                const key = normalizeGraniteMaterialKey(item?.key)
                if (!key) return

                const preset = item?.texturePreset && typeof item.texturePreset === 'object'
                    ? item.texturePreset
                    : {}
                const priceMeta = getAssemblyGraniteUnitPriceMeta({
                    catalog: item,
                    dimensions: priceDimensions,
                    blankType: priceBlankType,
                })
                const followsMonument = key === parentKey
                const baseSizeLabel = priceMeta.sizeLabel || formatAssemblyPriceDimensions(priceDimensions)

                result.push({
                    key,
                    label: String(item?.name || key),
                    textureUrl: String(preset?.url || '').trim(),
                    configured: priceMeta.configured,
                    isDefault: followsMonument,
                    priceLabel: priceMeta.priceLabel,
                    sizeLabel: [
                        followsMonument ? 'Как у стелы' : '',
                        baseSizeLabel,
                    ].filter(Boolean).join(' · '),
                    hint: followsMonument
                        ? 'Текущий гранит стелы. При выборе этого пункта элемент снова наследует материал стелы.'
                        : 'Отдельный гранит элемента. Цена за 1 изделие из каталога заготовок.',
                })
            })

        if (!inherited && overrideKey && !selectedMaterial) {
            result.push({
                key: overrideKey,
                label: `Старая настройка — ${overrideKey}`,
                textureUrl: String(material?.appearance?.url || '').trim(),
                configured: false,
                priceLabel: 'Legacy-настройка',
                sizeLabel: formatAssemblyPriceDimensions(priceDimensions),
                hint: 'Сохранено для совместимости со старой сценой',
            })
        }

        return result
    }, [
        graniteMaterials,
        inherited,
        material?.appearance?.url,
        overrideKey,
        parentKey,
        priceBlankType,
        priceDimensions,
        selectedMaterial,
    ])

    const handleChange = (value) => {
        const key = normalizeGraniteMaterialKey(value)

        // Текущий камень стелы не дублируем отдельным пунктом «Как у стелы».
        // Клик по самой карточке этого камня включает наследование.
        if (key && key === parentKey) {
            onChange?.({
                graniteSource: MONUMENT_GRANITE_SOURCE.PARENT,
                materialKey: '',
                textureKey: 'parent',
                appearance: 'original',
            })
            return
        }

        const catalog = graniteMaterials.find((item) => (
            normalizeGraniteMaterialKey(item?.key) === key
        )) || null
        const preset = catalog?.texturePreset && typeof catalog.texturePreset === 'object'
            ? catalog.texturePreset
            : null

        onChange?.({
            graniteSource: MONUMENT_GRANITE_SOURCE.CUSTOM,
            materialKey: key,
            textureKey: key,
            appearance: preset ? { ...preset, materialKey: key } : material?.appearance || 'original',
            color: '#ffffff',
            roughness: Number.isFinite(Number(preset?.roughness)) ? Number(preset.roughness) : material?.roughness,
            metalness: Number.isFinite(Number(preset?.metalness)) ? Number(preset.metalness) : material?.metalness,
        })
    }

    return (
        <div className="monument-assembly-editor__selectField monument-assembly-editor__field--full">
            <span>{label}</span>

            <div className="categoryGranitePicker">
                <GraniteTextureRail
                    items={railItems}
                    activeKey={selectedValue}
                    disabled={disabled}
                    collapsible
                    defaultCollapsed
                    collapseLabel={label}
                    expandedHint="Цена за 1 изделие · подписка / обычная"
                    contextLabel="Подписка / обычная цена"
                    showContext
                    onChange={handleChange}
                    ariaLabel={label}
                />
            </div>

            <small>
                {inherited
                    ? `Наследует гранит стелы: ${inheritedLabel}. Цена в списке указана за 1 изделие.`
                    : `Отдельный гранит: ${selectedMaterial?.name || overrideKey || 'не выбран'}. Цена в списке указана за 1 изделие.`}
            </small>
        </div>
    )
}

const makeSvgTexture = (svg) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`

const CONCRETE_TEXTURE_PALETTE = Object.freeze({
    'Бетон светлый': makeSvgTexture(`
        <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
            <rect width="128" height="128" fill="#a9aaab"/>
            <g fill="#8f9294" opacity=".42">
                <circle cx="14" cy="18" r="2"/><circle cx="46" cy="31" r="1.5"/><circle cx="81" cy="15" r="2.2"/>
                <circle cx="112" cy="39" r="1.7"/><circle cx="25" cy="72" r="2.4"/><circle cx="66" cy="63" r="1.6"/>
                <circle cx="99" cy="87" r="2.1"/><circle cx="44" cy="109" r="1.8"/><circle cx="120" cy="118" r="1.5"/>
            </g>
            <g stroke="#c9caca" stroke-width="1" opacity=".32">
                <path d="M0 24L128 8M0 78L128 55M0 118L128 98"/>
            </g>
        </svg>
    `),
    'Бетон серый': makeSvgTexture(`
        <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
            <rect width="128" height="128" fill="#777b7e"/>
            <g fill="#55595c" opacity=".55">
                <circle cx="17" cy="13" r="2.2"/><circle cx="39" cy="47" r="1.6"/><circle cx="74" cy="26" r="2"/>
                <circle cx="106" cy="12" r="1.3"/><circle cx="119" cy="65" r="2.4"/><circle cx="83" cy="92" r="1.7"/>
                <circle cx="29" cy="102" r="2.1"/><circle cx="61" cy="119" r="1.4"/>
            </g>
            <g fill="#a3a6a8" opacity=".34">
                <circle cx="8" cy="57" r="1.2"/><circle cx="52" cy="75" r="1.8"/><circle cx="96" cy="49" r="1.4"/>
            </g>
        </svg>
    `),
})

const BEDDING_TEXTURE_PALETTE = Object.freeze({
    'Песок': makeSvgTexture(`
        <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
            <rect width="128" height="128" fill="#b59a72"/>
            <g fill="#8f7656" opacity=".7">
                <circle cx="10" cy="15" r="1.5"/><circle cx="28" cy="7" r="1"/><circle cx="45" cy="24" r="1.4"/>
                <circle cx="67" cy="12" r="1.2"/><circle cx="92" cy="27" r="1.7"/><circle cx="119" cy="9" r="1.1"/>
                <circle cx="17" cy="54" r="1.2"/><circle cx="38" cy="69" r="1.8"/><circle cx="73" cy="51" r="1"/>
                <circle cx="105" cy="66" r="1.5"/><circle cx="123" cy="92" r="1.3"/><circle cx="58" cy="101" r="1.6"/>
                <circle cx="22" cy="117" r="1.1"/><circle cx="87" cy="121" r="1.5"/>
            </g>
            <g fill="#d6be93" opacity=".62">
                <circle cx="20" cy="32" r="1"/><circle cx="55" cy="44" r="1.3"/><circle cx="86" cy="77" r="1.1"/>
                <circle cx="113" cy="112" r="1.4"/><circle cx="35" cy="91" r="1.2"/>
            </g>
        </svg>
    `),
    'Щебень': makeSvgTexture(`
        <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
            <rect width="128" height="128" fill="#756f67"/>
            <g stroke="#45413d" stroke-width="1" opacity=".8">
                <path fill="#9a9389" d="M4 18l10-9 12 6-3 13-14 2z"/><path fill="#68635d" d="M38 7l13 3 4 12-9 9-12-6z"/>
                <path fill="#aaa197" d="M69 12l12-8 11 9-4 15-14 1z"/><path fill="#5d5954" d="M103 5l17 6 2 14-13 8-12-10z"/>
                <path fill="#8a847c" d="M10 47l14-8 11 9-2 15-15 5-11-9z"/><path fill="#aaa49b" d="M45 43l16-5 10 11-5 16-17 2-8-12z"/>
                <path fill="#625e58" d="M82 44l12-10 15 7 4 15-11 12-16-4z"/><path fill="#969087" d="M8 87l11-12 15 4 7 14-10 13-17-2z"/>
                <path fill="#595550" d="M52 82l12-9 15 8 1 15-13 9-15-8z"/><path fill="#aaa39a" d="M91 83l13-8 16 7 3 14-11 13-17-3z"/>
                <path fill="#858078" d="M26 112l13-8 13 8-2 14H30z"/><path fill="#625d57" d="M69 111l12-9 14 8-1 16H73z"/>
            </g>
        </svg>
    `),
    'Песок со щебнем': makeSvgTexture(`
        <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
            <rect width="128" height="128" fill="#9a876c"/>
            <g fill="#5f5a53" stroke="#4a4641" stroke-width=".8">
                <path d="M8 20l8-7 9 5-2 10-11 2z"/><path d="M47 10l10 4 2 9-8 7-9-6z"/>
                <path d="M83 25l9-8 11 5 1 10-8 7-11-4z"/><path d="M20 72l9-7 10 5-1 11-9 5-10-6z"/>
                <path d="M65 61l11-5 9 7-2 10-10 4-9-7z"/><path d="M101 91l10-6 10 6-1 11-9 5-10-6z"/>
            </g>
            <g fill="#d0b98e" opacity=".72">
                <circle cx="18" cy="43" r="1.3"/><circle cx="34" cy="51" r="1"/><circle cx="58" cy="39" r="1.5"/>
                <circle cx="78" cy="96" r="1.2"/><circle cx="43" cy="111" r="1.4"/><circle cx="116" cy="47" r="1.1"/>
            </g>
        </svg>
    `),
})

const normalizeDraftNumber = (value) => String(value ?? '').replace(',', '.')

const NumberField = ({ label, value, min, max, step = 10, unit = 'мм', onChange, disabled = false }) => {
    const [draft, setDraft] = useState(() => normalizeDraftNumber(value))
    const [editing, setEditing] = useState(false)

    useEffect(() => {
        if (!editing) setDraft(normalizeDraftNumber(value))
    }, [editing, value])

    const commitDraft = useCallback(() => {
        const parsed = Number.parseFloat(String(draft || '').replace(',', '.'))
        const fallback = Number(value)
        const safeFallback = Number.isFinite(fallback) ? fallback : Number(min) || 1
        const next = Number.isFinite(parsed)
            ? Math.min(Number(max) || parsed, Math.max(Number(min) || 0, parsed))
            : safeFallback

        setEditing(false)
        setDraft(normalizeDraftNumber(next))

        if (next !== fallback) onChange?.(next)
    }, [draft, max, min, onChange, value])

    return (
        <label className="monument-assembly-editor__numberField">
            <span>{label}</span>
            <div className="monument-assembly-editor__numberInputWrap">
                <input
                    type="text"
                    inputMode="decimal"
                    value={draft}
                    disabled={disabled}
                    onFocus={(event) => {
                        setEditing(true)
                        event.currentTarget.select?.()
                    }}
                    onChange={(event) => {
                        const next = event.target.value
                        if (/^-?\d*[.,]?\d*$/.test(next)) setDraft(next)
                    }}
                    onBlur={commitDraft}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            event.currentTarget.blur()
                        }

                        if (event.key === 'Escape') {
                            event.preventDefault()
                            setEditing(false)
                            setDraft(normalizeDraftNumber(value))
                            event.currentTarget.blur()
                        }

                        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                            event.preventDefault()
                            const current = Number.parseFloat(String(draft || '').replace(',', '.'))
                            const base = Number.isFinite(current) ? current : Number(value) || Number(min) || 1
                            const delta = event.key === 'ArrowUp' ? Number(step) || 1 : -(Number(step) || 1)
                            const next = Math.min(Number(max) || base + delta, Math.max(Number(min) || 0, base + delta))
                            setDraft(normalizeDraftNumber(next))
                        }
                    }}
                />
                {unit ? <span>{unit}</span> : null}
            </div>
        </label>
    )
}

const formatDimensionValue = (value) => {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? Math.round(number) : 0
}

const formatMoney = (value, empty = 'Цена не задана') => {
    const number = Number(value)
    if (!Number.isFinite(number) || number <= 0) return empty
    return `${Math.round(number).toLocaleString('ru-RU')} ₽`
}

const formatArea = (value) => {
    const number = Number(value)
    if (!Number.isFinite(number) || number <= 0) return '0 м²'
    return `${number.toLocaleString('ru-RU', { maximumFractionDigits: 3 })} м²`
}

const formatCatalogLineDimensions = (line) => {
    if (!line?.dimensions) return ''

    const dimensions = line.dimensions
    const ordered = [
        dimensions.width,
        dimensions.height,
        dimensions.depth ?? dimensions.length,
    ].map((value) => formatDimensionValue(value)).filter(Boolean)

    return ordered.length ? `${ordered.join('×')} мм` : ''
}

const getSectionForAccessoryRole = (role) => {
    switch (role) {
        case MONUMENT_ASSEMBLY_PRODUCT_ROLES.pedestal:
            return EDITOR_SECTIONS.pedestal
        case MONUMENT_ASSEMBLY_PRODUCT_ROLES.bedding:
            return EDITOR_SECTIONS.bedding
        case MONUMENT_ASSEMBLY_PRODUCT_ROLES.foundation:
            return EDITOR_SECTIONS.foundation
        case MONUMENT_ASSEMBLY_PRODUCT_ROLES.tile:
            return EDITOR_SECTIONS.tile
        case MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb:
        case MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb:
            return EDITOR_SECTIONS.flowerbed
        case MONUMENT_ASSEMBLY_PRODUCT_ROLES.ledger:
        case MONUMENT_ASSEMBLY_PRODUCT_ROLES.grass:
            return EDITOR_SECTIONS.ledger
        default:
            return null
    }
}

const AssemblyIcon = ({ type }) => {
    const commonProps = {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.7,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        'aria-hidden': true,
    }

    if (type === EDITOR_SECTIONS.pedestal) {
        return (
            <svg {...commonProps}>
                <path d="M4 15h16v4H4z" />
                <path d="M7 11h10v4H7z" />
            </svg>
        )
    }

    if (type === EDITOR_SECTIONS.bedding) {
        return (
            <svg {...commonProps}>
                <path d="M3.5 17.5c2.3-4.7 5.4-7 8.7-7 3.2 0 6 2.2 8.3 7" />
                <circle cx="8" cy="15" r=".8" />
                <circle cx="12" cy="13" r=".8" />
                <circle cx="16" cy="15.5" r=".8" />
            </svg>
        )
    }

    if (type === EDITOR_SECTIONS.foundation) {
        return (
            <svg {...commonProps}>
                <path d="M3.5 9.5 12 6l8.5 3.5L12 13z" />
                <path d="M3.5 9.5v5L12 18l8.5-3.5v-5" />
            </svg>
        )
    }

    if (type === EDITOR_SECTIONS.tile) {
        return (
            <svg {...commonProps}>
                <rect x="4" y="4" width="16" height="16" rx="2" />
                <path d="M9.33 4v16M14.66 4v16M4 9.33h16M4 14.66h16" />
            </svg>
        )
    }

    if (type === EDITOR_SECTIONS.flowerbed) {
        return (
            <svg {...commonProps}>
                <path d="M5 5v13h14V5" />
                <path d="M8 5v10h8V5" />
            </svg>
        )
    }

    if (type === EDITOR_SECTIONS.ledger) {
        return (
            <svg {...commonProps}>
                <path d="M5 17h14" />
                <path d="M7 17V8l5-3 5 3v9" />
                <path d="M9.5 10.5h5" />
            </svg>
        )
    }

    if (type === EDITOR_SECTIONS.position) {
        return (
            <svg {...commonProps}>
                <path d="M12 3v18M3 12h18" />
                <path d="m9 6 3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3" />
            </svg>
        )
    }

    return (
        <svg {...commonProps}>
            <path d="M7 6h13M7 12h13M7 18h13" />
            <circle cx="4" cy="6" r="1" />
            <circle cx="4" cy="12" r="1" />
            <circle cx="4" cy="18" r="1" />
        </svg>
    )
}

const SectionPanel = ({ type, title, status, selected = false, onClose, children }) => (
    <section className={[
        'monument-assembly-editor__sectionPanel',
        selected ? 'is-scene-selected' : '',
    ].filter(Boolean).join(' ')}>
        <div className="monument-assembly-editor__sectionHead">
            <span className="monument-assembly-editor__sectionIcon">
                <AssemblyIcon type={type} />
            </span>
            <span className="monument-assembly-editor__sectionHeading">
                <strong>{title}</strong>
                <small>{status}</small>
            </span>
            <button
                type="button"
                className="monument-assembly-editor__sectionClose"
                onClick={onClose}
                aria-label={`Закрыть раздел «${title}»`}
            >
                ×
            </button>
        </div>
        <div className="monument-assembly-editor__sectionBody">
            {children}
        </div>
    </section>
)

// Монтажные работы WRK, разрешённые категорией памятника (кэш на офис + категорию).
// Кэш живёт 30 с и обновляется при возврате на вкладку — новые тарифы видны без F5.
const SCENE_WORKS_TTL_MS = 30000
const sceneWorksCache = new Map()
const useSceneInstallationWorks = (officeId, categoryId) => {
    const key = officeId && categoryId ? `${officeId}:${categoryId}` : ''
    const [state, setState] = useState(() => sceneWorksCache.get(key)?.value || null)
    const [refresh, setRefresh] = useState(0)
    useEffect(() => {
        const onFocus = () => setRefresh((value) => value + 1)
        window.addEventListener('focus', onFocus)
        return () => window.removeEventListener('focus', onFocus)
    }, [])
    useEffect(() => {
        if (!key) { setState(null); return undefined }
        const cached = sceneWorksCache.get(key)
        if (cached) setState(cached.value)
        if (cached && Date.now() - cached.at < SCENE_WORKS_TTL_MS) return undefined
        let alive = true
        axiosInstance.get(`/office/${encodeURIComponent(officeId)}/installation-works/scene`, { params: { categoryId } })
            .then(({ data }) => {
                const value = { templates: Array.isArray(data?.templates) ? data.templates : [], configured: data?.configured === true }
                sceneWorksCache.set(key, { value, at: Date.now() })
                if (alive) setState(value)
            })
            .catch(() => { if (alive && !cached) setState({ templates: [], configured: false, error: true }) })
        return () => { alive = false }
    }, [key, officeId, categoryId, refresh])
    return state
}
const isFixedSizeWork = (template) => (
    Number(template?.lengthMm) > 0 && Number(template?.widthMm) > 0
    && template?.foundationScope === 'slab' && template?.pricingMode === 'fixed'
)
const workPriceText = (template) => {
    if (!template) return ''
    if (template.pricingMode === 'manual') return 'по оценке'
    const unit = { volume: '/м³', area: '/м²', post: '/столб' }[template.pricingMode] || ''
    return `${(Number(template.rate) || 0).toLocaleString('ru-RU')} ₽${unit}`
}

const MonumentAssemblyEditor = ({
    productCategoryId = '',
    officeId: officeIdProp = '',
    value = null,
    monumentDimensions = {},
    parentAppearance = null,
    parentMaterialKey = '',
    onChange,
    onApplyToAll,
    onApplyTileToAll,
    onApplyPartToAll,
    onSelectAccessory,
    selectedAccessoryId = '',
    disabled = false,
    compact = false,
    embedded = false,
    bulkMode = false,
    bulkCount = 0,
    // Путь выделенного объекта сцены (из TransformControls); иначе — selectedModel из store.
    targetModelId = '',
}) => {
    const [open, setOpen] = useState(() => embedded)
    const [activeSection, setActiveSection] = useState(null)
    // Выделенный объект — «кирпич» (товар без 3D-файла)? Тогда даём его настройки прямо здесь.
    // Выделена деталь комплекта (подставка, поребрик) — смотрим на её родителя.
    const brickPath = useSelector((state) => {
        const selected = String(targetModelId || state.scene?.selectedModel || '')
        const parent = parseMonumentAccessorySceneId(selected)?.parentModelId || selected
        if (!parent) return ''
        const modelsData = state.office?.modelsData || {}
        const baseId = getSceneObjectBaseId(parent) || parent
        return (modelsData[baseId] || modelsData[parent])?.product?.procedural?.type === 'brick' ? parent : ''
    })
    const brickModelData = useSelector((state) => {
        if (!brickPath) return null
        const modelsData = state.office?.modelsData || {}
        return modelsData[getSceneObjectBaseId(brickPath) || brickPath] || modelsData[brickPath] || null
    })
    const brickProcedural = brickModelData?.product?.procedural?.type === 'brick'
        ? brickModelData.product.procedural
        : null
    const brickAppearance = useSelector((state) => (
        brickPath ? state.scene?.colors?.instanceByPath?.[brickPath] ?? null : null
    ))
    const dispatch = useDispatch()
    // Сам кирпич выделен на сцене (не деталь комплекта) — для авто-открытия блока «Кирпич».
    const sceneSelectedModel = useSelector((state) => String(state.scene?.selectedModel || ''))
    const sceneSelectedIsBrick = useSelector((state) => {
        const selected = String(state.scene?.selectedModel || '')
        if (!selected || parseMonumentAccessorySceneId(selected)) return false
        const modelsData = state.office?.modelsData || {}
        const baseId = getSceneObjectBaseId(selected) || selected
        return (modelsData[baseId] || modelsData[selected])?.product?.procedural?.type === 'brick'
    })
    const lastSceneSelectionRef = useRef('')
    const storeOfficeId = useSelector((state) => state.office?.currentOfficeId || '')
    const officeId = String(officeIdProp || storeOfficeId || '')
    const sceneWorks = useSceneInstallationWorks(officeId, String(productCategoryId || ''))
    const assembly = useMemo(() => (
        normalizeMonumentAssembly(value || {}, monumentDimensions)
    ), [monumentDimensions, value])
    const assemblyRef = useRef(assembly)
    const foundationWork = useMemo(() => (
        assembly.foundation?.workTemplateId
            ? (sceneWorks?.templates || []).find((template) => String(template.id) === String(assembly.foundation.workTemplateId)) || null
            : null
    ), [assembly.foundation?.workTemplateId, sceneWorks])
    // Сплошная заливка: ленточные работы и демонтаж не предлагаем.
    // С размером в шаблоне — задают заливку; без размера (м³, м², оценка) — к своему размеру.
    const fixedFoundationWorks = useMemo(() => (
        (sceneWorks?.templates || []).filter((template) => !template.demolition && template.foundationScope !== 'strip' && isFixedSizeWork(template))
    ), [sceneWorks])
    // Тариф для «По участку» / «Свой размер»: работа с флагом customFoundation (м³ или м²).
    const customFoundationWork = useMemo(() => (
        (sceneWorks?.templates || []).find((template) => template.customFoundation && !template.demolition
            && template.foundationScope !== 'strip' && ['volume', 'area'].includes(template.pricingMode)) || null
    ), [sceneWorks])
    const customFoundationQty = useMemo(() => {
        const w = Number(assembly.foundation?.width) || 0
        const l = Number(assembly.foundation?.depth) || 0
        const h = Number(assembly.foundation?.height) || 0
        return customFoundationWork?.pricingMode === 'area'
            ? Math.round((w * l) / 1e6 * 1000) / 1000
            : Math.round((w * l * h) / 1e9 * 1000) / 1000
    }, [assembly.foundation, customFoundationWork])

    useEffect(() => {
        assemblyRef.current = assembly
    }, [assembly])

    useEffect(() => {
        if (embedded && !open) setOpen(true)
    }, [embedded, open])

    const catalogLines = useMemo(() => (
        getMonumentAssemblyCatalogLines(assembly, monumentDimensions)
    ), [assembly, monumentDimensions])
    const autoPedestalWidth = useMemo(() => (
        getAutoPedestalWidthMm(monumentDimensions?.width || assembly.monumentDimensions?.width || 500)
    ), [assembly.monumentDimensions?.width, monumentDimensions?.width])
    const tileMetrics = useMemo(() => (
        getMonumentTileMetrics(assembly, monumentDimensions)
    ), [assembly, monumentDimensions])
    const beddingMetrics = useMemo(() => (
        getMonumentBeddingMetrics(assembly, monumentDimensions)
    ), [assembly, monumentDimensions])
    const knownPricing = useMemo(() => (
        getMonumentAssemblyKnownPrice(assembly, monumentDimensions)
    ), [assembly, monumentDimensions])
    // Цены деталей — как в корзине: товар офиса из «3D-комплекта» (подходящий размер,
    // похожий — с пометкой), иначе встроенная цена детали.
    const kitVersion = useScene3dKitVersion()
    const sectionPricing = useMemo(() => {
        const out = {}
        const shortCurb = catalogLines.find((line) => line?.role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb)
        const longCurb = catalogLines.find((line) => line?.role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb)
        catalogLines.forEach((line) => {
            const section = getSectionForAccessoryRole(line?.role)
            if (!section) return
            const kit = line?.productId
                ? null
                : resolveKitAssemblyLine({ ...line, shortLength: shortCurb?.dimensions?.length, longLength: longCurb?.dimensions?.length })
            const total = Number(kit ? kit.total : line?.total) || 0
            const entry = out[section] || { total: 0, missing: false, lines: [] }
            entry.total += total
            if (total <= 0 && !kit?.includedInSet) entry.missing = true
            entry.lines.push({
                title: kit?.title || line?.title || '',
                article: kit?.product?.article || '',
                note: kit?.note || '',
                total,
                included: kit?.includedInSet === true,
                fromKit: !!kit,
            })
            out[section] = entry
        })
        return out
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogLines, kitVersion])
    const sectionPriceText = (section) => {
        const entry = sectionPricing[section]
        if (!entry) return ''
        if (entry.total > 0) return formatMoney(entry.total)
        return section === 'foundation' ? (foundationWork ? `в ${foundationWork.article}` : 'по монтажу WRK') : 'цена не задана'
    }
    const sectionsTotal = Object.values(sectionPricing).reduce((sum, entry) => sum + entry.total, 0)
    const withPrice = (text, section) => [text, sectionPriceText(section)].filter(Boolean).join(' · ')
    const {
        materials: blankMaterials = [],
        defaultMaterialKey: defaultBlankMaterialKey = 'gabbro-diabase',
    } = useBlankMaterialCatalog()
    const graniteMaterials = useMemo(() => (
        (Array.isArray(blankMaterials) ? blankMaterials : [])
            .filter((material) => (
                material?.enabled !== false &&
                material?.key &&
                !isSurfacePseudoCatalog(material) &&
                material?.texturePreset?.url
            ))
    ), [blankMaterials])
    const inheritedGraniteKey = useMemo(() => normalizeGraniteMaterialKey(
        parentMaterialKey ||
        getGraniteMaterialKeyFromAppearance(parentAppearance) ||
        defaultBlankMaterialKey ||
        graniteMaterials[0]?.key ||
        'gabbro-diabase'
    ), [
        defaultBlankMaterialKey,
        graniteMaterials,
        parentAppearance,
        parentMaterialKey,
    ])
    // Гранит кирпича = гранит самого камня (как «Гранит памятника»).
    const brickGraniteKey = normalizeGraniteMaterialKey(
        getGraniteMaterialKeyFromAppearance(brickAppearance)
        || getMonumentGraniteKeyFromAppearance(brickAppearance)
        || defaultBlankMaterialKey
        || ''
    )
    const brickGraniteItems = useMemo(() => {
        if (!brickProcedural) return []
        const dimensions = {
            height: Number(brickProcedural.heightMm) || 0,
            width: Number(brickProcedural.widthMm) || 0,
            thickness: Number(brickProcedural.thicknessMm) || 0,
        }
        return graniteMaterials.map((item) => {
            const key = normalizeGraniteMaterialKey(item?.key)
            const priceMeta = getAssemblyGraniteUnitPriceMeta({ catalog: item, dimensions })
            return {
                key,
                label: String(item?.name || key),
                textureUrl: String(item?.texturePreset?.url || '').trim(),
                configured: priceMeta.configured,
                isDefault: key === brickGraniteKey,
                priceLabel: priceMeta.priceLabel,
                sizeLabel: priceMeta.sizeLabel || formatAssemblyPriceDimensions(dimensions),
                hint: 'Гранит камня. Цена за 1 изделие из каталога заготовок.',
            }
        })
    }, [brickGraniteKey, brickProcedural, graniteMaterials])
    const handleBrickGraniteChange = (value) => {
        const key = normalizeGraniteMaterialKey(value)
        const catalog = graniteMaterials.find((item) => normalizeGraniteMaterialKey(item?.key) === key)
        if (!brickPath || !catalog) return
        const selection = createGraniteSceneSelection({
            materialCatalog: catalog,
            unpolishedSurfaceCatalog: getUnpolishedSurfaceCatalog(blankMaterials),
            surfaceFinishPolicy: brickModelData?.product?.surfaceFinishPolicy || {},
        })
        dispatch({ type: 'SET_COLORS', payload: { instanceByPath: { [brickPath]: { __granite: selection } } } })
    }
    const getGraniteMaterialLabel = useCallback((material, fallback = 'Гранит') => {
        const inherited = isMonumentGraniteInherited(material)
        const key = inherited
            ? inheritedGraniteKey
            : getMonumentGraniteOverrideKey(material)
        const catalog = graniteMaterials.find((item) => (
            normalizeGraniteMaterialKey(item?.key) === normalizeGraniteMaterialKey(key)
        ))

        return catalog?.name || key || fallback
    }, [graniteMaterials, inheritedGraniteKey])
    const selectedAccessory = useMemo(() => (
        parseMonumentAccessorySceneId(selectedAccessoryId)
    ), [selectedAccessoryId])
    const selectedSection = useMemo(() => (
        getSectionForAccessoryRole(selectedAccessory?.role || '')
    ), [selectedAccessory?.role])

    useEffect(() => {
        if (!selectedAccessoryId || !selectedSection) return
        setOpen(true)
        setActiveSection(selectedSection)
    }, [selectedAccessoryId, selectedSection])

    // Открываем «Кирпич» только в момент смены выделения на сам кирпич.
    // Выделение детали (поребрик, подставка) сюда не попадает — их открывает эффект выше.
    useEffect(() => {
        if (lastSceneSelectionRef.current === sceneSelectedModel) return
        lastSceneSelectionRef.current = sceneSelectedModel
        if (!sceneSelectedIsBrick || !brickProcedural) return
        setOpen(true)
        setActiveSection(EDITOR_SECTIONS.brick)
    }, [sceneSelectedModel, sceneSelectedIsBrick, brickProcedural])

    const emitNextAssembly = (nextAssembly) => {
        assemblyRef.current = nextAssembly
        onChange?.(nextAssembly)
    }

    // Сегменты-плитки вместо флажков и выпадающих списков (стили — _assembly-compact.scss).
    const renderSegment = ({ title, items, value, onPick, chips = false, isDisabled = false, note = '' }) => (
        <div className="monument-assembly-editor__group monument-assembly-editor__field--full">
            {title ? <div className="monument-assembly-editor__groupTitle">{title}</div> : null}
            <div className="monument-assembly-editor__segment">
                {items.map((item) => (
                    <button
                        key={String(item.key)}
                        type="button"
                        title={item.hint || ''}
                        disabled={disabled || isDisabled}
                        className={[
                            'monument-assembly-editor__segmentBtn',
                            chips ? 'monument-assembly-editor__segmentBtn--chip' : '',
                            value === item.key ? 'is-active' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => onPick(item.key)}
                    >
                        <b>{item.label}</b>
                        {!chips && item.hint ? <span>{item.hint}</span> : null}
                    </button>
                ))}
            </div>
            {note ? <small className="monument-assembly-editor__groupNote">{note}</small> : null}
        </div>
    )

    // Полировка детали комплекта: как обычно или «1 ст.» — только верхняя сторона.
    const renderPartPolish = (part, standardHint) => renderSegment({
        title: 'Полировка',
        value: assembly[part]?.polishMode || 'standard',
        items: [
            { key: 'standard', label: 'Как обычно', hint: standardHint },
            { key: 'top', label: '1 ст.', hint: 'только верхняя сторона' },
        ],
        onPick: (polishMode) => commit({ [part]: { polishMode } }),
    })

    const commit = (patch) => {
        const current = assemblyRef.current || assembly
        emitNextAssembly(patchMonumentAssembly(current, patch, monumentDimensions))
    }

    const setPlotMode = (plotMode) => {
        commit({
            enabled: plotMode !== 'none' || assembly.pedestal.enabled || assembly.foundation.enabled,
            plotMode,
            flowerbed: { enabled: assembly.flowerbed.enabled },
        })
    }

    const getSectionAccessoryTarget = useCallback((sectionId) => {
        if (sectionId === EDITOR_SECTIONS.pedestal && assembly.pedestal.enabled) {
            return { sectionId, role: assembly.pedestal.product.role, quantityIndex: 1 }
        }

        if (sectionId === EDITOR_SECTIONS.bedding && assembly.bedding.enabled) {
            return { sectionId, role: assembly.bedding.product.role, quantityIndex: 1 }
        }

        if (sectionId === EDITOR_SECTIONS.foundation && assembly.foundation.enabled) {
            return { sectionId, role: assembly.foundation.product.role, quantityIndex: 1 }
        }

        if (sectionId === EDITOR_SECTIONS.tile && assembly.tile.enabled) {
            return { sectionId, role: assembly.tile.product.role, quantityIndex: 1 }
        }

        if (sectionId === EDITOR_SECTIONS.flowerbed && assembly.flowerbed.enabled) {
            return { sectionId, role: assembly.flowerbed.longCurbProduct.role, quantityIndex: 1 }
        }

        if (sectionId === EDITOR_SECTIONS.ledger && assembly.ledger.enabled) {
            return { sectionId, role: assembly.ledger.product.role, quantityIndex: 1 }
        }

        if (sectionId === EDITOR_SECTIONS.ledger && assembly.grass.enabled) {
            return { sectionId, role: assembly.grass.product.role, quantityIndex: 1 }
        }

        return null
    }, [assembly])

    const toggleSection = (sectionId) => {
        const opening = activeSection !== sectionId
        setActiveSection(opening ? sectionId : null)

        if (!opening) return

        const target = getSectionAccessoryTarget(sectionId)
        if (target) onSelectAccessory?.(target)
    }

    const tileMaterialLabel = assembly.tile.materialType === 'porcelain'
        ? 'Керамогранит'
        : getGraniteMaterialLabel(assembly.tile.material, 'Гранит')
    const pedestalMaterialLabel = getGraniteMaterialLabel(
        assembly.pedestal.material,
        'Камень'
    )
    const ledgerStatus = assembly.ledger.enabled
        ? `${assembly.ledger.width}×${assembly.ledger.length} мм`
        : assembly.grass.enabled
            ? `Покрытие ${assembly.grass.width}×${assembly.grass.length} мм`
            : 'Выключена'

    const brickState = { ...(brickProcedural || {}), ...(assembly.brick || {}) }
    if (!BRICK_POLISH_OPTIONS.some((item) => item.key === brickState.polish)) brickState.polish = 'five'
    const brickPolishLabel = (BRICK_POLISH_OPTIONS.find((item) => item.key === brickState.polish) || BRICK_POLISH_OPTIONS[0]).label
    const sectionItems = [
        // Кирпич — настройки одного камня; показываем и когда на сцене несколько памятников (bulkMode).
        ...(brickProcedural ? [{
            id: EDITOR_SECTIONS.brick,
            label: 'Кирпич',
            enabled: true,
            status: `${brickState.heightMm / 10}×${brickState.widthMm / 10}×${brickState.thicknessMm / 10} · ${brickPolishLabel}${brickState.double ? ' · двойник' : ''}${brickState.chamferMm ? ` · фаска ${brickState.chamferMm}` : ''}`,
        }] : []),
        {
            id: EDITOR_SECTIONS.pedestal,
            label: 'Подставка',
            enabled: assembly.pedestal.enabled,
            status: assembly.pedestal.enabled
                ? withPrice(`${assembly.pedestal.width} мм · ${pedestalMaterialLabel}`, EDITOR_SECTIONS.pedestal)
                : 'Выключена',
        },
        {
            id: EDITOR_SECTIONS.bedding,
            label: 'Подсыпка',
            enabled: assembly.bedding.enabled,
            status: assembly.bedding.enabled
                ? `${formatArea(beddingMetrics.areaSquareMeters)} · ${formatMoney(beddingMetrics.total, '0 ₽')}`
                : 'Выключена',
        },
        {
            id: EDITOR_SECTIONS.foundation,
            label: 'Заливка',
            enabled: assembly.foundation.enabled,
            status: assembly.foundation.enabled
                ? withPrice(`${assembly.foundation.width}×${assembly.foundation.depth} мм`, EDITOR_SECTIONS.foundation)
                : 'Выключена',
        },
        {
            id: EDITOR_SECTIONS.tile,
            label: 'Плитка',
            enabled: assembly.tile.enabled,
            status: assembly.tile.enabled
                ? withPrice(`${tileMetrics.quantity} шт. · ${tileMaterialLabel}`, EDITOR_SECTIONS.tile)
                : 'Выключена',
        },
        {
            id: EDITOR_SECTIONS.flowerbed,
            label: 'Цветник',
            enabled: assembly.flowerbed.enabled,
            status: assembly.flowerbed.enabled
                ? withPrice('3 элемента', EDITOR_SECTIONS.flowerbed)
                : 'Выключен',
        },
        {
            id: EDITOR_SECTIONS.ledger,
            label: 'Надгробка',
            enabled: assembly.ledger.enabled || assembly.grass.enabled,
            status: assembly.ledger.enabled || assembly.grass.enabled
                ? withPrice(ledgerStatus, EDITOR_SECTIONS.ledger)
                : ledgerStatus,
        },
        {
            id: EDITOR_SECTIONS.position,
            label: 'Положение',
            enabled: !!(assembly.upperOffset.x || assembly.upperOffset.y),
            status: assembly.upperOffset.x || assembly.upperOffset.y
                ? `${assembly.upperOffset.x} / ${assembly.upperOffset.y} мм`
                : 'По центру',
        },
        {
            id: EDITOR_SECTIONS.composition,
            label: 'Состав',
            enabled: catalogLines.length > 0,
            status: `${catalogLines.length} поз. · ${formatMoney(sectionsTotal || knownPricing.total, '0 ₽')}`,
        },
    ]

    const activeSectionItem = sectionItems.find((item) => item.id === activeSection) || null
    const bulkTargetLabel = bulkCount > 0 ? ` всем ${bulkCount}` : ' ко всем'
    const renderBulkApplyAction = (part, label, extraDisabled = false) => {
        if (!bulkMode || typeof onApplyPartToAll !== 'function') return null

        return (
            <div className="monument-assembly-editor__compactActions monument-assembly-editor__compactActions--bulk">
                <button
                    type="button"
                    className="is-active"
                    disabled={disabled || extraDisabled}
                    onClick={() => onApplyPartToAll(part)}
                >
                    {label}{bulkTargetLabel}
                </button>
            </div>
        )
    }

    return (
        <div className={[
            'monument-assembly-editor',
            compact ? 'is-compact' : '',
            embedded ? 'is-embedded' : '',
            bulkMode ? 'is-bulk-mode' : '',
            open ? 'is-open' : 'is-collapsed',
        ].filter(Boolean).join(' ')}>
            {!embedded ? (
            <button
                type="button"
                className="monument-assembly-editor__head"
                onClick={() => setOpen((previous) => !previous)}
                aria-expanded={open}
            >
                <span className="monument-assembly-editor__headIcon">
                    <AssemblyIcon type={EDITOR_SECTIONS.composition} />
                </span>
                <span className="monument-assembly-editor__headText">
                    <strong>Комплект памятника</strong>
                    <small>{catalogLines.length ? `${catalogLines.length} позиций в комплекте` : 'Комплект не настроен'}</small>
                </span>
                <span className="monument-assembly-editor__headTotal">
                    {formatMoney(knownPricing.total, '0 ₽')}
                </span>
                <span className="monument-assembly-editor__headChevron" aria-hidden="true">⌄</span>
            </button>
            ) : null}

            {(embedded || open) ? (
                <div className="monument-assembly-editor__body">
                    <div className="monument-assembly-editor__sectionGrid">
                        {sectionItems.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={[
                                    'monument-assembly-editor__sectionButton',
                                    item.enabled ? 'is-enabled' : '',
                                    activeSection === item.id ? 'is-active' : '',
                                    selectedSection === item.id ? 'is-scene-selected' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => toggleSection(item.id)}
                                aria-pressed={activeSection === item.id}
                            >
                                <span className="monument-assembly-editor__sectionButtonIcon">
                                    <AssemblyIcon type={item.id} />
                                </span>
                                <strong>{item.label}</strong>
                                <small>{item.status}</small>
                            </button>
                        ))}
                    </div>

                    {activeSectionItem ? (
                        <SectionPanel
                            type={activeSectionItem.id}
                            title={activeSectionItem.label}
                            status={activeSectionItem.status}
                            selected={selectedSection === activeSectionItem.id}
                            onClose={() => setActiveSection(null)}
                        >
                            {/* Заливка: цена — в выбранной установке ниже, отдельный блок цены не нужен. */}
                            {/* Заливка и плитка: цена — в своём блоке ниже, отдельный блок цены не нужен. */}
                            {sectionPricing[activeSectionItem.id] && activeSectionItem.id !== EDITOR_SECTIONS.foundation && activeSectionItem.id !== EDITOR_SECTIONS.tile ? (
                                <div className={`monument-assembly-editor__priceBox${sectionPricing[activeSectionItem.id].missing ? ' is-missing' : ''}`}>
                                    {sectionPricing[activeSectionItem.id].lines.map((line, index) => (
                                        <div key={`${line.title}-${index}`} className="monument-assembly-editor__priceLine">
                                            <span>
                                                {line.title}
                                                {line.article ? <em> · арт. {line.article}</em> : null}
                                                {line.note ? <small>{line.note}</small> : null}
                                            </span>
                                            <b>{line.included ? 'в комплекте' : line.total > 0 ? formatMoney(line.total) : 'цена не задана'}</b>
                                        </div>
                                    ))}
                                    {activeSectionItem.id === 'foundation' && foundationWork ? (
                                        <div className="monument-assembly-editor__priceHint">
                                            Заливка входит в работу {foundationWork.article} «{foundationWork.name}» — {workPriceText(foundationWork)}. В бланке и заказе: «включено в монтаж».
                                        </div>
                                    ) : sectionPricing[activeSectionItem.id].missing ? (
                                        <div className="monument-assembly-editor__priceHint">
                                            {activeSectionItem.id === 'foundation'
                                                ? 'Отдельного товара заливки нет. Стоимость учтёт работа WRK с основанием в «Печать формы → Работы на месте» (заливка будет «включено в монтаж»). Если заливка продаётся отдельно — назначьте товар в Товары → 3D-комплект.'
                                                : 'Нет товара в «3D-комплекте» офиса — подберите его в Товары → 3D-комплект.'}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {['pedestal', 'flowerbed', 'ledger'].includes(activeSection) && assembly[activeSection]?.enabled ? (
                                renderSegment({
                                    title: 'Фаска по кромкам',
                                    chips: true,
                                    isDisabled: activeSection === 'pedestal' && assembly.pedestal.variant === 'bevel',
                                    value: assembly[activeSection].edgeBevelEnabled ? Number(assembly[activeSection].edgeBevelMm) || 0 : 0,
                                    items: [0, 1, 2, 3, 5, 8, 10].map((mm) => ({ key: mm, label: mm ? `${mm} мм` : 'Нет' })),
                                    onPick: (mm) => commit({ [activeSection]: mm ? { edgeBevelEnabled: true, edgeBevelMm: mm } : { edgeBevelEnabled: false } }),
                                    note: activeSection === 'pedestal' && assembly.pedestal.variant === 'bevel'
                                        ? 'У подставки со скосом свой профиль — фаска только у ровной.'
                                        : 'Срез между полированными сторонами — полированный, рядом с неполированной — нет.',
                                })
                            ) : null}
                            {activeSection === EDITOR_SECTIONS.pedestal ? (
                                <>
                                    <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--section">
                                        <input
                                            type="checkbox"
                                            checked={assembly.pedestal.enabled}
                                            disabled={disabled}
                                            onChange={(event) => commit({
                                                enabled: event.target.checked || assembly.foundation.enabled || assembly.plotMode !== 'none',
                                                pedestal: { enabled: event.target.checked },
                                            })}
                                        />
                                        <span>Использовать подставку</span>
                                    </label>

                                    {assembly.pedestal.enabled ? (
                                        <>
                                            <div className="monument-assembly-editor__fields">
                                                {renderSegment({
                                                    title: 'Тип',
                                                    value: assembly.pedestal.variant === 'bevel' ? 'bevel' : 'flat',
                                                    items: [
                                                        { key: 'flat', label: 'Ровная', hint: 'прямые грани' },
                                                        { key: 'bevel', label: 'Баян-скос', hint: 'скос спереди' },
                                                    ],
                                                    onPick: (variant) => commit({ pedestal: { variant, polishedBevel: variant === 'bevel' } }),
                                                })}
                                                <label className="monument-assembly-editor__selectField">
                                                    <span>Ширина</span>
                                                    <select
                                                        value={assembly.pedestal.sizeMode === 'monument'
                                                            ? 'auto'
                                                            : String(assembly.pedestal.width)}
                                                        disabled={disabled}
                                                        onChange={(event) => {
                                                            const nextValue = event.target.value

                                                            if (nextValue === 'auto') {
                                                                commit({ pedestal: { sizeMode: 'monument', width: autoPedestalWidth, depth: 200 } })
                                                                return
                                                            }

                                                            commit({
                                                                pedestal: {
                                                                    sizeMode: 'manual',
                                                                    width: Number(nextValue) || autoPedestalWidth,
                                                                    depth: 200,
                                                                }
                                                            })
                                                        }}
                                                    >
                                                        <option value="auto">Авто — {autoPedestalWidth} мм</option>
                                                        {MONUMENT_PEDESTAL_WIDTHS_MM.map((width) => (
                                                            <option key={width} value={width}>{width} мм</option>
                                                        ))}
                                                    </select>
                                                </label>
                                                {renderSegment({
                                                    title: 'Высота',
                                                    chips: true,
                                                    value: Number(assembly.pedestal.height) === 120 ? 120 : 150,
                                                    items: [120, 150].map((mm) => ({ key: mm, label: `${mm} мм` })),
                                                    onPick: (height) => commit({ pedestal: { height } }),
                                                })}
                                                <div className="monument-assembly-editor__priceCard">
                                                    <span>1 шт.</span>
                                                    <b>{formatMoney(MONUMENT_PEDESTAL_FIXED_PRICE_RUB)}</b>
                                                </div>
                                            </div>

                                            <GraniteAssemblyMaterialSelect
                                                label="Гранит подставки"
                                                material={assembly.pedestal.material}
                                                parentMaterialKey={inheritedGraniteKey}
                                                graniteMaterials={graniteMaterials}
                                                priceDimensions={{
                                                    height: assembly.pedestal.width,
                                                    width: assembly.pedestal.depth,
                                                    thickness: assembly.pedestal.height,
                                                }}
                                                priceBlankType="base"
                                                disabled={disabled}
                                                onChange={(material) => commit({ pedestal: { material } })}
                                            />

                                            {assembly.pedestal.variant !== 'bevel'
                                                ? renderPartPolish('pedestal', 'верх и четыре боковые стороны')
                                                : null}

                                            {assembly.pedestal.variant === 'bevel' ? (
                                                <div className="monument-assembly-editor__checkGrid">
                                                    <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--small">
                                                        <input
                                                            type="checkbox"
                                                            checked={assembly.pedestal.polishedBevel}
                                                            disabled={disabled}
                                                            onChange={(event) => commit({ pedestal: { polishedBevel: event.target.checked } })}
                                                        />
                                                        <span>Полировать скос</span>
                                                    </label>
                                                    <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--small">
                                                        <input
                                                            type="checkbox"
                                                            checked={assembly.pedestal.epitaphEnabled}
                                                            disabled={disabled}
                                                            onChange={(event) => commit({ pedestal: { epitaphEnabled: event.target.checked } })}
                                                        />
                                                        <span>Текст на скосе</span>
                                                    </label>
                                                </div>
                                            ) : null}
                                        </>
                                    ) : null}
                                    {renderBulkApplyAction('pedestal', 'Применить подставку', !assembly.pedestal.enabled)}
                                </>
                            ) : null}

                            {activeSection === EDITOR_SECTIONS.bedding ? (
                                <>
                                    <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--section">
                                        <input
                                            type="checkbox"
                                            checked={assembly.bedding.enabled}
                                            disabled={disabled}
                                            onChange={(event) => commit({
                                                enabled: event.target.checked || assembly.pedestal.enabled || assembly.foundation.enabled || assembly.tile.enabled || assembly.plotMode !== 'none',
                                                bedding: { enabled: event.target.checked },
                                            })}
                                        />
                                        <span>Использовать подсыпку</span>
                                    </label>

                                    {assembly.bedding.enabled ? (
                                        <>
                                            <div className="monument-assembly-editor__fields">
                                                <NumberField label="Ширина" value={assembly.bedding.width} min={300} max={10000} disabled={disabled} onChange={(width) => commit({ bedding: { width } })} />
                                                <NumberField label="Длина" value={assembly.bedding.length} min={300} max={12000} disabled={disabled} onChange={(length) => commit({ bedding: { length } })} />
                                                <label className="monument-assembly-editor__selectField">
                                                    <span>Толщина</span>
                                                    <select
                                                        value={String(assembly.bedding.height)}
                                                        disabled={disabled}
                                                        onChange={(event) => commit({ bedding: { height: Number(event.target.value) === 100 ? 100 : 50 } })}
                                                    >
                                                        <option value="50">50 мм</option>
                                                        <option value="100">100 мм</option>
                                                    </select>
                                                </label>
                                                <NumberField label="Цена за м²" value={assembly.bedding.pricePerSquareMeter} unit="₽" min={0} max={1000000} step={100} disabled={disabled} onChange={(pricePerSquareMeter) => commit({ bedding: { pricePerSquareMeter } })} />
                                            </div>

                                            <ColorSelector
                                                label="Фактура подсыпки"
                                                value={assembly.bedding.material?.appearance || 'original'}
                                                onChange={(nextValue) => commit({ bedding: { material: { appearance: getColorSelectorValue(nextValue) } } })}
                                                colors={BEDDING_TEXTURE_PALETTE}
                                                textureOnly
                                                tuningCollapsible
                                                tuningDefaultOpen={false}
                                                disabled={disabled}
                                            />

                                            <div className="monument-assembly-editor__compactActions">
                                                <button type="button" disabled={disabled} onClick={() => commit({ bedding: { width: 2500, length: 3000 } })}>
                                                    Размер 2500×3000 мм
                                                </button>
                                            </div>

                                            <div className="monument-assembly-editor__priceCard monument-assembly-editor__priceCard--wide">
                                                <span>{formatArea(beddingMetrics.areaSquareMeters)} · {formatMoney(beddingMetrics.unitPrice)} / м²</span>
                                                <b>{formatMoney(beddingMetrics.total)}</b>
                                            </div>
                                        </>
                                    ) : null}
                                    {renderBulkApplyAction('bedding', 'Применить подсыпку', !assembly.bedding.enabled)}
                                </>
                            ) : null}

                            {activeSection === EDITOR_SECTIONS.foundation ? (
                                <>
                                    <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--section">
                                        <input
                                            type="checkbox"
                                            checked={assembly.foundation.enabled}
                                            disabled={disabled}
                                            onChange={(event) => commit({
                                                enabled: event.target.checked || assembly.pedestal.enabled || assembly.plotMode !== 'none',
                                                foundation: { enabled: event.target.checked },
                                            })}
                                        />
                                        <span>Использовать заливку</span>
                                    </label>

                                    {assembly.foundation.enabled ? (
                                        <>
                                            <div className="monument-assembly-editor__works">
                                                <div className="monument-assembly-editor__worksHead">
                                                    <b>Заливка и установка</b>
                                                    <small>Один выбор: установка WRK задаёт размер заливки и попадает в бланк и заказ. Или задайте размер сами.</small>
                                                </div>
                                                <div className="monument-assembly-editor__worksList" role="radiogroup" aria-label="Заливка и установка">
                                                    {fixedFoundationWorks.map((template) => {
                                                        const active = assembly.foundation.sizeMode === 'work' && String(assembly.foundation.workTemplateId) === String(template.id)
                                                        return (
                                                            <button
                                                                key={template.id}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={active}
                                                                className={`monument-assembly-editor__work${active ? ' is-active' : ''}`}
                                                                disabled={disabled}
                                                                onClick={() => commit({ foundation: {
                                                                    sizeMode: 'work',
                                                                    workTemplateId: String(template.id),
                                                                    width: Math.round(Number(template.widthMm)),
                                                                    depth: Math.round(Number(template.lengthMm)),
                                                                    ...(Number(template.thicknessMm) > 0 ? { height: Math.round(Number(template.thicknessMm)) } : {}),
                                                                } })}
                                                            >
                                                                <span>
                                                                    <b>{template.name}</b>
                                                                    <small>{template.article} · заливка {Math.round(Number(template.widthMm))}×{Math.round(Number(template.lengthMm))}{Number(template.thicknessMm) > 0 ? `×${Math.round(Number(template.thicknessMm))}` : ''} мм</small>
                                                                </span>
                                                                <em>{workPriceText(template)}</em>
                                                            </button>
                                                        )
                                                    })}
                                                    {[
                                                        ['bedding', 'По участку', `Как подсыпка: ${assembly.bedding.width}×${assembly.bedding.length} мм`],
                                                        ['manual', 'Свой размер', 'Ширина, длина и высота вручную'],
                                                    ].map(([mode, label, hint]) => {
                                                        const active = (assembly.foundation.sizeMode || 'standard') === mode
                                                        return (
                                                            <button
                                                                key={mode}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={active}
                                                                className={`monument-assembly-editor__work${active ? ' is-active' : ''}`}
                                                                disabled={disabled}
                                                                onClick={() => commit({ foundation: {
                                                                    sizeMode: mode,
                                                                    // Свой размер / по участку — тариф заливки по м³ или м² (если назначен).
                                                                    workTemplateId: customFoundationWork ? String(customFoundationWork.id) : '',
                                                                } })}
                                                            >
                                                                <span><b>{label}</b><small>{hint}</small></span>
                                                            </button>
                                                        )
                                                    })}
                                                </div>

                                                {['bedding', 'manual'].includes(assembly.foundation.sizeMode) ? (
                                                    customFoundationWork ? (
                                                        <div className="monument-assembly-editor__worksNote">
                                                            {customFoundationWork.pricingMode === 'area'
                                                                ? `${customFoundationQty.toLocaleString('ru-RU')} м² × ${workPriceText(customFoundationWork)}`
                                                                : `${customFoundationQty.toLocaleString('ru-RU')} м³ × ${workPriceText(customFoundationWork)}`}
                                                            {' = '}<b>{Math.round(customFoundationQty * (Number(customFoundationWork.rate) || 0)).toLocaleString('ru-RU')} ₽</b>
                                                            {` · ${customFoundationWork.article}`}
                                                        </div>
                                                    ) : sceneWorks ? (
                                                        <small className="monument-assembly-editor__worksNote is-warning">
                                                            Нет тарифа для своего размера. Назначьте его: «Установки и работы» → «Тарифы» → «Заливка своего размера в 3D».
                                                        </small>
                                                    ) : null
                                                ) : null}

                                                {!officeId ? (
                                                    <small className="monument-assembly-editor__worksNote">Работы WRK доступны в калькуляторе офиса.</small>
                                                ) : !productCategoryId ? (
                                                    <small className="monument-assembly-editor__worksNote">Не удалось определить категорию памятника — работу можно выбрать в бланке.</small>
                                                ) : !sceneWorks ? (
                                                    <small className="monument-assembly-editor__worksNote">Загружаем работы…</small>
                                                ) : !sceneWorks.templates.length ? (
                                                    <small className="monument-assembly-editor__worksNote">Для категории не назначены монтажные работы — «Категории» → «3D и работы» → «Монтаж».</small>
                                                ) : null}
                                            </div>

                                            <div className="monument-assembly-editor__fields">
                                                <NumberField label="Ширина" value={assembly.foundation.width} min={300} max={10000} disabled={disabled || assembly.foundation.sizeMode !== 'manual'} onChange={(width) => commit({ foundation: { width, sizeMode: 'manual' } })} />
                                                <NumberField label="Длина" value={assembly.foundation.depth} min={300} max={12000} disabled={disabled || assembly.foundation.sizeMode !== 'manual'} onChange={(depth) => commit({ foundation: { depth, sizeMode: 'manual' } })} />
                                                <NumberField label="Высота" value={assembly.foundation.height} min={20} max={500} disabled={disabled || assembly.foundation.sizeMode === 'work'} onChange={(height) => commit({ foundation: { height } })} />
                                                {assembly.foundation.sizeMode === 'manual' || assembly.foundation.sizeMode === 'bedding' ? (
                                                    <button
                                                        type="button"
                                                        className="monument-assembly-editor__rotateBtn"
                                                        disabled={disabled}
                                                        title="Поменять ширину и длину местами"
                                                        onClick={() => commit({ foundation: { sizeMode: 'manual', width: assembly.foundation.depth, depth: assembly.foundation.width } })}
                                                    >
                                                        ⇄ Повернуть на 90°
                                                    </button>
                                                ) : null}
                                            </div>

                                            <ColorSelector
                                                label="Фактура заливки"
                                                value={assembly.foundation.material?.appearance || 'original'}
                                                onChange={(nextValue) => commit({ foundation: { material: { appearance: getColorSelectorValue(nextValue) } } })}
                                                colors={CONCRETE_TEXTURE_PALETTE}
                                                textureOnly
                                                tuningCollapsible
                                                tuningDefaultOpen={false}
                                                disabled={disabled}
                                            />
                                        </>
                                    ) : null}
                                </>
                            ) : null}

                            {activeSection === EDITOR_SECTIONS.tile ? (
                                <>
                                    <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--section">
                                        <input
                                            type="checkbox"
                                            checked={assembly.tile.enabled}
                                            disabled={disabled}
                                            onChange={(event) => commit({
                                                enabled: event.target.checked || assembly.bedding.enabled || assembly.pedestal.enabled || assembly.foundation.enabled || assembly.plotMode !== 'none',
                                                tile: { enabled: event.target.checked },
                                            })}
                                        />
                                        <span>Использовать плитку</span>
                                    </label>

                                    {assembly.tile.enabled ? (
                                        <>
                                            <div className="monument-assembly-editor__fields">
                                                <label className="monument-assembly-editor__selectField">
                                                    <span>Раскладка</span>
                                                    <select
                                                        value={assembly.tile.layout}
                                                        disabled={disabled}
                                                        onChange={(event) => {
                                                            const layout = event.target.value
                                                            const definition = MONUMENT_TILE_LAYOUTS[layout]
                                                            const materialType = definition?.porcelainAllowed
                                                                ? assembly.tile.materialType
                                                                : 'granite'

                                                            commit({
                                                                tile: {
                                                                    layout,
                                                                    materialType,
                                                                    pricePerPiece: materialType === 'porcelain'
                                                                        ? MONUMENT_PORCELAIN_TILE_PRICE_RUB
                                                                        : 0,
                                                                    height: 10,
                                                                    material: materialType === 'porcelain'
                                                                        ? { ...assembly.tile.material, preset: 'porcelain', color: '#d8d6cf', graniteSource: undefined, materialKey: '', textureKey: 'porcelain', appearance: 'original' }
                                                                        : { ...assembly.tile.material, preset: 'grayGranite', color: '#ffffff', graniteSource: MONUMENT_GRANITE_SOURCE.PARENT, materialKey: '', textureKey: 'parent', appearance: 'original' },
                                                                },
                                                            })
                                                        }}
                                                    >
                                                        {MONUMENT_TILE_LAYOUT_IDS.map((layoutId) => (
                                                            <option key={layoutId} value={layoutId}>{MONUMENT_TILE_LAYOUTS[layoutId].label}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                                <label className="monument-assembly-editor__selectField">
                                                    <span>Материал</span>
                                                    <select
                                                        value={assembly.tile.materialType}
                                                        disabled={disabled}
                                                        onChange={(event) => {
                                                            const requested = event.target.value === 'porcelain' ? 'porcelain' : 'granite'
                                                            const definition = MONUMENT_TILE_LAYOUTS[assembly.tile.layout]
                                                            const materialType = requested === 'porcelain' && definition?.porcelainAllowed
                                                                ? 'porcelain'
                                                                : 'granite'

                                                            commit({
                                                                tile: {
                                                                    materialType,
                                                                    pricePerPiece: materialType === 'porcelain'
                                                                        ? MONUMENT_PORCELAIN_TILE_PRICE_RUB
                                                                        : 0,
                                                                    height: 10,
                                                                    material: materialType === 'porcelain'
                                                                        ? { ...assembly.tile.material, preset: 'porcelain', color: '#d8d6cf', graniteSource: undefined, materialKey: '', textureKey: 'porcelain', appearance: 'original' }
                                                                        : { ...assembly.tile.material, preset: 'grayGranite', color: '#ffffff', graniteSource: MONUMENT_GRANITE_SOURCE.PARENT, materialKey: '', textureKey: 'parent', appearance: 'original' },
                                                                },
                                                            })
                                                        }}
                                                    >
                                                        {MONUMENT_TILE_LAYOUTS[assembly.tile.layout]?.porcelainAllowed ? (
                                                            <option value="porcelain">Керамогранит — 450 ₽/шт.</option>
                                                        ) : null}
                                                        <option value="granite">Гранит</option>
                                                    </select>
                                                </label>
                                                <NumberField label="Толщина" value={assembly.tile.height} min={5} max={100} disabled={disabled} onChange={(height) => commit({ tile: { height } })} />
                                                {assembly.tile.materialType === 'granite' ? (
                                                    <NumberField label="Цена за шт." value={assembly.tile.pricePerPiece || 0} unit="₽" min={0} max={1000000} step={100} disabled={disabled} onChange={(pricePerPiece) => commit({ tile: { pricePerPiece } })} />
                                                ) : null}
                                            </div>

                                            {assembly.tile.materialType === 'granite' ? (
                                                <GraniteAssemblyMaterialSelect
                                                    label="Гранит плитки"
                                                    material={assembly.tile.material}
                                                    parentMaterialKey={inheritedGraniteKey}
                                                    graniteMaterials={graniteMaterials}
                                                    priceDimensions={{
                                                        height: tileMetrics.pieceLength,
                                                        width: tileMetrics.pieceWidth,
                                                        thickness: assembly.tile.height,
                                                    }}
                                                    priceBlankType="slab"
                                                    disabled={disabled}
                                                    onChange={(material) => commit({ tile: { material } })}
                                                />
                                            ) : null}

                                            {bulkMode
                                                ? renderBulkApplyAction('tile', 'Применить плитку', !assembly.tile.enabled)
                                                : typeof onApplyTileToAll === 'function' ? (
                                                    <div className="monument-assembly-editor__compactActions">
                                                        <button type="button" disabled={disabled} onClick={onApplyTileToAll}>
                                                            Плитку на все товары витрины
                                                        </button>
                                                    </div>
                                                ) : null}

                                            <div className="monument-assembly-editor__priceCard monument-assembly-editor__priceCard--wide">
                                                <span>{tileMetrics.pieceWidth}×{tileMetrics.pieceLength} мм · {tileMetrics.quantity} шт. · {formatArea(tileMetrics.coveredAreaSquareMeters)}</span>
                                                <b>{formatMoney(tileMetrics.total, assembly.tile.materialType === 'granite' ? 'Цена гранита не задана' : '0 ₽')}</b>
                                            </div>
                                        </>
                                    ) : null}
                                </>
                            ) : null}

                            {activeSection === EDITOR_SECTIONS.flowerbed ? (
                                <>
                                    <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--section">
                                        <input
                                            type="checkbox"
                                            checked={assembly.flowerbed.enabled}
                                            disabled={disabled}
                                            onChange={(event) => commit({ flowerbed: { enabled: event.target.checked } })}
                                        />
                                        <span>Использовать цветник</span>
                                    </label>

                                    {assembly.flowerbed.enabled ? (
                                        <>
                                            <div className="monument-assembly-editor__fields">
                                                <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--small">
                                                    <input
                                                        type="checkbox"
                                                        checked={assembly.flowerbed.autoOuterWidth}
                                                        disabled={disabled}
                                                        onChange={(event) => commit({ flowerbed: { autoOuterWidth: event.target.checked } })}
                                                    />
                                                    <span>Ширина по подставке</span>
                                                </label>
                                                <NumberField label="Наружная ширина" value={assembly.flowerbed.outerWidth} min={250} max={4000} disabled={disabled} onChange={(outerWidth) => commit({ flowerbed: { outerWidth, autoOuterWidth: false } })} />
                                                {renderSegment({
                                                    title: 'Длинные поребрики · 2 шт.',
                                                    chips: true,
                                                    value: Number(assembly.flowerbed.length) || 1000,
                                                    items: [800, 1000, 1200, 1500, 2000].map((mm) => ({ key: mm, label: `${mm}` })),
                                                    onPick: (length) => commit({ flowerbed: { length, lengthManual: length !== 1000 } }),
                                                })}
                                                <NumberField label="Своя длина" unit="мм" value={assembly.flowerbed.length} min={300} max={2500} step={50} disabled={disabled} onChange={(length) => commit({ flowerbed: { length, lengthManual: Number(length) !== 1000 } })} />
                                                <NumberField label="Ширина поребрика" value={assembly.flowerbed.curbWidth} min={30} max={500} disabled={disabled} onChange={(curbWidth) => commit({ flowerbed: { curbWidth } })} />
                                                <NumberField label="Высота поребрика" value={assembly.flowerbed.curbHeight} min={30} max={500} disabled={disabled} onChange={(curbHeight) => commit({ flowerbed: { curbHeight } })} />
                                                {renderPartPolish('flowerbed', 'верх и лицевые стороны')}
                                            </div>

                                            <div className="monument-assembly-editor__materialGrid">
                                                <div className={selectedAccessory?.role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb ? 'is-selected' : ''}>
                                                    <GraniteAssemblyMaterialSelect
                                                        label="Длинные поребрики"
                                                        material={assembly.flowerbed.longCurbMaterial}
                                                        parentMaterialKey={inheritedGraniteKey}
                                                        graniteMaterials={graniteMaterials}
                                                        priceDimensions={{
                                                            height: assembly.flowerbed.length,
                                                            width: assembly.flowerbed.curbWidth,
                                                            thickness: assembly.flowerbed.curbHeight,
                                                        }}
                                                        priceBlankType="flower-bed"
                                                        disabled={disabled}
                                                        onChange={(longCurbMaterial) => commit({ flowerbed: { longCurbMaterial } })}
                                                    />
                                                </div>
                                                <div className={selectedAccessory?.role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb ? 'is-selected' : ''}>
                                                    <GraniteAssemblyMaterialSelect
                                                        label="Короткий поребрик"
                                                        material={assembly.flowerbed.shortCurbMaterial}
                                                        parentMaterialKey={inheritedGraniteKey}
                                                        graniteMaterials={graniteMaterials}
                                                        priceDimensions={{
                                                            height: assembly.flowerbed.outerWidth,
                                                            width: assembly.flowerbed.curbWidth,
                                                            thickness: assembly.flowerbed.curbHeight,
                                                        }}
                                                        priceBlankType="flower-bed"
                                                        disabled={disabled}
                                                        onChange={(shortCurbMaterial) => commit({ flowerbed: { shortCurbMaterial } })}
                                                    />
                                                </div>
                                            </div>

                                            <div className="monument-assembly-editor__priceCard monument-assembly-editor__priceCard--wide">
                                                {/* Цена цветника — как в корзине: из «3D-комплекта» офиса (подробно — в плашке сверху). */}
                                                <span>Цветник: 2 длинных + короткий</span>
                                                <b>{sectionPriceText(EDITOR_SECTIONS.flowerbed) || formatMoney(MONUMENT_LONG_CURB_UNIT_PRICE_RUB * 2 + MONUMENT_SHORT_CURB_FIXED_PRICE_RUB)}</b>
                                            </div>
                                        </>
                                    ) : null}
                                    {renderBulkApplyAction('flowerbed', 'Применить цветник', !assembly.flowerbed.enabled)}
                                </>
                            ) : null}

                            {activeSection === EDITOR_SECTIONS.ledger ? (
                                <>
                                    <label className="monument-assembly-editor__selectField monument-assembly-editor__field--full">
                                        <span>Вариант заполнения</span>
                                        <select value={assembly.ledger.enabled ? 'ledger' : assembly.grass.enabled ? 'grass' : 'none'} disabled={disabled} onChange={(event) => setPlotMode(event.target.value)}>
                                            <option value="none">Без надгробки</option>
                                            <option value="ledger">Надгробная плита</option>
                                            <option value="grass">Трава / покрытие</option>
                                        </select>
                                    </label>

                                    {assembly.ledger.enabled ? (
                                        <>
                                            <div className="monument-assembly-editor__compactActions">
                                                <button type="button" disabled={disabled || !assembly.flowerbed.enabled} onClick={() => commit({ ledger: { autoWidth: false, width: assembly.flowerbed.outerWidth, length: assembly.flowerbed.length + assembly.flowerbed.curbWidth } })}>По цветнику</button>
                                                {assembly.flowerbed.enabled ? <small>{assembly.flowerbed.outerWidth} × {assembly.flowerbed.length + assembly.flowerbed.curbWidth} мм</small> : null}
                                            </div>
                                            <div className="monument-assembly-editor__fields">
                                                <label className="monument-assembly-editor__selectField">
                                                    <span>Форма</span>
                                                    <select
                                                        value={assembly.ledger.style}
                                                        disabled={disabled}
                                                        onChange={(event) => commit({ ledger: { style: event.target.value === 'shaped' ? 'shaped' : 'flat' } })}
                                                    >
                                                        <option value="flat">Ровная</option>
                                                        <option value="shaped">Фигурная</option>
                                                    </select>
                                                </label>
                                                <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--small">
                                                    <input
                                                        type="checkbox"
                                                        checked={assembly.ledger.autoWidth}
                                                        disabled={disabled}
                                                        onChange={(event) => commit({ ledger: { autoWidth: event.target.checked } })}
                                                    />
                                                    <span>Ширина по подставке</span>
                                                </label>
                                                <NumberField label="Ширина" value={assembly.ledger.width} min={250} max={4000} disabled={disabled} onChange={(width) => commit({ ledger: { width, autoWidth: false } })} />
                                                <NumberField label="Длина" value={assembly.ledger.length} min={300} max={5000} disabled={disabled} onChange={(length) => commit({ ledger: { length } })} />
                                                <NumberField label="Высота" value={assembly.ledger.height} min={20} max={500} disabled={disabled} onChange={(height) => commit({ ledger: { height } })} />
                                            </div>

                                            <GraniteAssemblyMaterialSelect
                                                label="Гранит надгробной плиты"
                                                material={assembly.ledger.material}
                                                parentMaterialKey={inheritedGraniteKey}
                                                graniteMaterials={graniteMaterials}
                                                priceDimensions={{
                                                    height: assembly.ledger.length,
                                                    width: assembly.ledger.width,
                                                    thickness: assembly.ledger.height,
                                                }}
                                                priceBlankType="slab"
                                                disabled={disabled}
                                                onChange={(material) => commit({ ledger: { material } })}
                                            />

                                            {renderPartPolish('ledger', '4 ст — верх, передний торец и два бока')}
                                            <small>{assembly.ledger.polishMode === 'top'
                                                ? 'Полировка: 1 ст — только верх.'
                                                : 'Полировка: 4 ст — верх, передний торец и два бока. Торец у подставки и низ — неполированные.'}</small>
                                            <small>Эпитафия и рисунок настраиваются в блоках «Эпитафия» и «Рисунок»: выберите поверхность «Надгробная плита».</small>

                                        </>
                                    ) : null}

                                    {assembly.grass.enabled ? (
                                        <div className="monument-assembly-editor__fields">
                                            <label className="monument-assembly-editor__toggle monument-assembly-editor__toggle--small">
                                                <input
                                                    type="checkbox"
                                                    checked={assembly.grass.autoWidth}
                                                    disabled={disabled}
                                                    onChange={(event) => commit({ grass: { autoWidth: event.target.checked } })}
                                                />
                                                <span>Ширина по подставке</span>
                                            </label>
                                            <NumberField label="Ширина" value={assembly.grass.width} min={250} max={4000} disabled={disabled} onChange={(width) => commit({ grass: { width, autoWidth: false } })} />
                                            <NumberField label="Длина" value={assembly.grass.length} min={300} max={5000} disabled={disabled} onChange={(length) => commit({ grass: { length } })} />
                                            <NumberField label="Толщина" value={assembly.grass.height} min={5} max={100} step={5} disabled={disabled} onChange={(height) => commit({ grass: { height } })} />
                                        </div>
                                    ) : null}
                                    {renderBulkApplyAction(
                                        assembly.grass.enabled ? 'grass' : 'ledger',
                                        assembly.grass.enabled ? 'Применить покрытие' : 'Применить надгробку',
                                        !assembly.ledger.enabled && !assembly.grass.enabled
                                    )}
                                </>
                            ) : null}

                            {activeSection === EDITOR_SECTIONS.brick && brickProcedural ? (
                                <div className="monument-assembly-editor__fields">
                                    {renderSegment({
                                        title: 'Полировка',
                                        value: brickState.polish,
                                        items: BRICK_POLISH_OPTIONS,
                                        onPick: (polish) => commit({ brick: { polish } }),
                                    })}
                                    <div className="monument-assembly-editor__selectField monument-assembly-editor__field--full">
                                        <span>Гранит кирпича</span>
                                        <div className="categoryGranitePicker">
                                            <GraniteTextureRail
                                                items={brickGraniteItems}
                                                activeKey={brickGraniteKey}
                                                disabled={disabled}
                                                collapsible
                                                defaultCollapsed
                                                collapseLabel="Гранит кирпича"
                                                expandedHint="Цена за 1 изделие · подписка / обычная"
                                                contextLabel="Подписка / обычная цена"
                                                showContext
                                                onChange={handleBrickGraniteChange}
                                                ariaLabel="Гранит кирпича"
                                            />
                                        </div>
                                    </div>
                                    {renderSegment({
                                        title: 'Положение',
                                        value: brickState.double === true,
                                        items: [
                                            { key: false, label: 'Стоя', hint: 'высота вверх' },
                                            { key: true, label: 'Двойник', hint: 'боком, длинной стороной' },
                                        ],
                                        onPick: (double) => commit({ brick: { double } }),
                                    })}
                                    {renderSegment({
                                        title: 'Фаска',
                                        chips: true,
                                        value: Number(brickState.chamferMm) || 0,
                                        items: [0, 2, 3, 5, 8, 10].map((mm) => ({ key: mm, label: mm ? `${mm} мм` : 'Нет' })),
                                        onPick: (chamferMm) => commit({ brick: { chamferMm } }),
                                        note: 'Другой размер — стрелками внизу экрана («Прямые комплекты памятников»).',
                                    })}
                                </div>
                            ) : null}

                            {activeSection === EDITOR_SECTIONS.position ? (
                                <>
                                    <div className="monument-assembly-editor__fields">
                                        <NumberField label="Влево / вправо" value={assembly.upperOffset.x} min={-5000} max={5000} step={10} disabled={disabled} onChange={(x) => commit({ upperOffset: { x } })} />
                                        <NumberField label="Назад / вперёд" value={assembly.upperOffset.y} min={-5000} max={5000} step={10} disabled={disabled} onChange={(y) => commit({ upperOffset: { y } })} />
                                    </div>
                                    <div className="monument-assembly-editor__compactActions">
                                        <button type="button" disabled={disabled || (!assembly.upperOffset.x && !assembly.upperOffset.y)} onClick={() => commit({ upperOffset: { x: 0, y: 0 } })}>
                                            Поставить по центру
                                        </button>
                                    </div>
                                </>
                            ) : null}

                            {activeSection === EDITOR_SECTIONS.composition ? (
                                <>
                                    {catalogLines.length ? (
                                        <div className="monument-assembly-editor__catalogList">
                                            {catalogLines.map((line) => {
                                                const dimensionsText = formatCatalogLineDimensions(line)
                                                const isLineSelected = selectedAccessory?.role === line.role

                                                return (
                                                    <div
                                                        key={`${line.role}:${line.quantity}:${line.variant || 'base'}`}
                                                        className={[
                                                            'monument-assembly-editor__catalogItem',
                                                            isLineSelected ? 'is-selected' : '',
                                                        ].filter(Boolean).join(' ')}
                                                    >
                                                        <span className="monument-assembly-editor__catalogItemMain">
                                                            <strong>{line.title}</strong>
                                                            <small>{dimensionsText || 'Размеры уточняются'}{line.quantity > 1 ? ` · ${line.quantity} шт.` : ''}</small>
                                                        </span>
                                                        <b>{line.pricing?.total > 0 ? formatMoney(line.pricing.total) : '—'}</b>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    ) : (
                                        <div className="monument-assembly-editor__emptyState">Комплект пока пуст.</div>
                                    )}

                                    <div className="monument-assembly-editor__knownTotal">
                                        <span>Итого по имитируемым элементам</span>
                                        <strong>{formatMoney(knownPricing.total, '0 ₽')}</strong>
                                    </div>

                                    {typeof onApplyToAll === 'function' ? (
                                        <div className="monument-assembly-editor__compactActions">
                                            <button type="button" disabled={disabled || !catalogLines.length} onClick={onApplyToAll}>
                                                {bulkCount > 0 ? `Применить весь комплект всем ${bulkCount}` : 'Применить комплект ко всем товарам'}
                                            </button>
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </SectionPanel>
                    ) : null}
                </div>
            ) : null}
        </div>
    )
}

export default memo(MonumentAssemblyEditor)
